import React, { useState } from "react";
import { formatNumberWithSpaces } from "../../utils";
import { ProductInterface, Subscription as SubscriptionInterface } from "../../interfaces/product/product.interface";

interface SubscriptionProps {
    product: ProductInterface;
    subscription: SubscriptionInterface;
    selectedSubscription?: SubscriptionInterface;
    onSelectSubscription?: (sub: SubscriptionInterface) => void;
    translate: Record<string, string>;
}

export const Subscription: React.FC<SubscriptionProps> = ({product, subscription, selectedSubscription, onSelectSubscription, translate}) => {
    const [showInfo, setShowInfo] = useState(false);
    const discount = subscription.discount || 0;
    const hasSpecialPrice = !!product.specialPrice;

    const normalizePrice = (p: any) => parseFloat(String(p).replace(/\s/g, "").replace(",", ".")) || 0;
    const basePrice = normalizePrice(product.price);
    const baseSpecialPrice = normalizePrice(product.specialPrice || "0");
    let specialPrice = null;

    if (!hasSpecialPrice) {
        if (discount > 0) {
            specialPrice = basePrice - discount;
            if (specialPrice <= 0) specialPrice = 1;
        }
    }else {
        specialPrice = baseSpecialPrice - discount;
        if (specialPrice <= 0) specialPrice = 1;
    }

    const isSelected = selectedSubscription?.position === subscription.position;

    const handleInfoClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowInfo(true);
    };

    const displayPrice = subscription.price
        ? formatNumberWithSpaces(Math.round(subscription.price))
        : '';

    const handleClosePopup = () => setShowInfo(false);

    return (
        <>
            <div
                className={`w-full flex flex-row justify-between items-center p-3 border bg-[#f7f8fa] mb-[4px] rounded-lg cursor-pointer transition subscription-block
                    ${isSelected ? "border-subscription" : "border-white"}`}
                onClick={() => onSelectSubscription?.(subscription)}
            >
                {/* Prices and Radio */}
                <div className="flex flex-row items-center justify-start md:mb-0 md:w-[100%] gap-[20px]">
                     <input
                        type="radio"
                        checked={isSelected}
                        onChange={() => onSelectSubscription?.(subscription)}
                        className="flex subscription-radio"
                        id={"input-radio-" + subscription.id}
                    />
                    <div className="flex flex-col" >
                        <div className="flex flex-row w-full items-center font-semibold gap-[20px]
                            [@media(min-width:900px)_and_(max-width:1400px)]:flex-col
                            [@media(min-width:900px)_and_(max-width:1400px)]:items-start
                            [@media(min-width:900px)_and_(max-width:1400px)]:gap-[0px]
                        ">
                            {/* Afișăm specialPrice dacă există, altfel basePrice */}
                            <p className="flex text-[20px] md:text-[24px] leading-[1]">
                                {formatNumberWithSpaces(specialPrice ?? basePrice)} {product.currency}
                            </p>

                            {/* Afișăm prețul tăiat doar dacă există specialPrice */}
                            {specialPrice > 0 && (
                                <p className="flex text-[14px] md:text-[20px] text-gray line-through decoration-[#E73140] leading-[1]">
                                    {formatNumberWithSpaces(basePrice)} {product.currency}
                                </p>
                            )}
                        </div>

                         <div className="flex flex-col">
                            <a className="text-left text-[14px] font-medium subscription-name line-clamp-3">
                            {subscription.price && subscription.price > 0
                                ? `${translate['textSubscribe']} ${subscription.name} ${translate['textConjunction']} ${displayPrice} ${translate.currencySubscribe}`
                                : subscription.name}
                            </a>
                        </div>
                    </div>
                </div>

                {/* Name */}
                <div className="flex flex-col items-end md:text-right md:ml-0 min-w-[50px]">
                    {(subscription.price > 0 &&
                        <div className="flex subscription-info" onClick={handleInfoClick}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17316C0.00433286 8.00042 -0.1937 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8078C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 7.34783 18.9464 4.80429 17.0711 2.92893C15.1957 1.05357 12.6522 0 10 0V0ZM10 18.3333C8.35183 18.3333 6.74066 17.8446 5.37025 16.9289C3.99984 16.0132 2.93174 14.7117 2.30101 13.189C1.67028 11.6663 1.50525 9.99075 1.82679 8.37425C2.14834 6.75774 2.94201 5.27288 4.10745 4.10744C5.27288 2.94201 6.75774 2.14833 8.37425 1.82679C9.99076 1.50525 11.6663 1.67027 13.189 2.301C14.7117 2.93173 16.0132 3.99984 16.9289 5.37025C17.8446 6.74066 18.3333 8.35182 18.3333 10C18.3333 12.2101 17.4554 14.3297 15.8926 15.8926C14.3298 17.4554 12.2101 18.3333 10 18.3333Z" fill="#949494"/>
                                <path d="M10.0004 8.08342C9.77938 8.08342 9.56742 8.17122 9.41114 8.3275C9.25486 8.48378 9.16706 8.69574 9.16706 8.91675V14.5834C9.16706 14.8044 9.25486 15.0164 9.41114 15.1727C9.56742 15.329 9.77938 15.4168 10.0004 15.4168C10.2214 15.4168 10.4334 15.329 10.5897 15.1727C10.7459 15.0164 10.8337 14.8044 10.8337 14.5834V8.91675C10.8337 8.69574 10.7459 8.48378 10.5897 8.3275C10.4334 8.17122 10.2214 8.08342 10.0004 8.08342ZM10.0004 4.66676C9.7748 4.67524 9.56101 4.76977 9.40294 4.93095C9.24486 5.09212 9.1545 5.3077 9.1504 5.53342V5.66675C9.15 5.77548 9.17225 5.8831 9.21571 5.98276C9.25918 6.08242 9.32292 6.17194 9.40287 6.24563C9.48283 6.31931 9.57725 6.37554 9.68012 6.41074C9.78299 6.44594 9.89206 6.45934 10.0004 6.45009C10.2188 6.44184 10.426 6.35139 10.5805 6.19686C10.735 6.04234 10.8255 5.83513 10.8337 5.61675V5.41675C10.8344 5.31084 10.8126 5.20599 10.7698 5.1091C10.727 5.01222 10.6642 4.92549 10.5855 4.85464C10.5067 4.78378 10.4139 4.73041 10.3131 4.69803C10.2122 4.66564 10.1057 4.65498 10.0004 4.66676V4.66676Z" fill="#949494"/>
                            </svg>
                        </div>
                    )}
                </div>
                {/* End */}
            </div>

            {/* Pop-up */}
            {showInfo && (
                <>
                    {/* Modal backdrop */}
                    <div
                        className={`starnet_background_modal active`}
                        onClick={handleClosePopup}
                    />

                    {/* Modal content */}
                    <div className={`starnet_content_modal active`}>
                        
                        <span className="starnet_close"
                            onClick={handleClosePopup}>
                            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.9243 0.367834C16.4152 0.858712 16.4157 1.655 15.925 2.14596L10.1466 7.92435L16.369 14.1467C16.8598 14.6377 16.8598 15.4333 16.369 15.9242C15.8781 16.4151 15.0818 16.4157 14.5909 15.9249L8.36916 9.70178L2.14607 15.9249C1.6551 16.4156 0.85881 16.4151 0.367943 15.9242C-0.122647 15.4333 -0.122677 14.6376 0.367943 14.1467L6.59103 7.92366L0.812647 2.14527C0.322004 1.6543 0.321802 0.858678 0.812647 0.367834C1.3035 -0.122882 2.09915 -0.122766 2.59008 0.367834L8.36847 6.14622L14.1469 0.367834C14.6377 -0.122816 15.4334 -0.122766 15.9243 0.367834Z" fill="#BDBDBD"/>
                            </svg>
                        </span>

                        <div
                            className="starnet_description"
                            dangerouslySetInnerHTML={{ __html: subscription.description }}
                        />


                        <div className="starnet_content_footer">
                            <div className="starnet_buttons">
                                <button type="button" onClick={handleClosePopup}>Ok</button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
